import {Shield, Cpu, Zap, Globe} from 'lucide-react'

const features = [
  {
    icon: <Shield size={20} className="text-indigo-600" />,
    title: 'Zero Data Retention',
    desc: 'Files never leave your device. Processing happens entirely within your browser using WebAssembly and the Canvas API.',
    bg: 'bg-indigo-50',
  },
  {
    icon: <Zap size={20} className="text-amber-600" />,
    title: 'Lightning Fast',
    desc: 'Heavy dependencies like PDF.js and Tesseract.js are lazy-loaded only when triggered — securing a 100/100 Core Web Vitals score.',
    bg: 'bg-amber-50',
  },
  {
    icon: <Cpu size={20} className="text-violet-600" />,
    title: 'WebAssembly Powered',
    desc: 'OCR, PDF rendering, and format parsing run on multi-threaded WASM — same speed as native desktop apps, no install needed.',
    bg: 'bg-violet-50',
  },
  {
    icon: <Globe size={20} className="text-emerald-600" />,
    title: 'Offline-First Design',
    desc: 'Once loaded, all tools work without an internet connection. Your workflow continues even when connectivity drops.',
    bg: 'bg-emerald-50',
  },
]

export default function TechFeatures() {
  return (
    <section className="py-20 px-4 bg-gray-50/50" aria-labelledby="features-heading">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs font-semibold text-indigo-600 uppercase tracking-widest mb-3">
            Technology
          </p>
          <h2
            id="features-heading"
            className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-3"
          >
            Built on browser-native tech
          </h2>
          <p className="text-base text-gray-500 max-w-xl mx-auto font-light">
            PDF.js, Tesseract.js, Canvas API, and WebAssembly threads — no cloud dependencies.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f) => (
            <div
              key={f.title}
              className="bg-white rounded-2xl p-6 border border-gray-100 hover:-translate-y-0.5 transition-transform"
              style={{ boxShadow: 'var(--shadow-card)' }}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${f.bg} mb-4`}>
                {f.icon}
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">{f.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed font-light">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* Comparison table: PDFTide vs MuPDF vs Adobe */}
        <div
          id="mupdf"
          className="mt-14 bg-white rounded-2xl border border-gray-100 overflow-hidden"
          style={{ boxShadow: 'var(--shadow-card)' }}
        >
          <div className="px-6 py-5 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900">
              PdfTide vs MuPDF vs Adobe Acrobat
            </h3>
            <p className="text-sm text-gray-400 mt-0.5">
              Web compilation vs local drivers — why browser-first wins
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" aria-label="PDF tool comparison: PdfTide, MuPDF, Adobe Acrobat">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Feature</th>
                  <th className="px-6 py-3 text-xs font-semibold text-indigo-700 uppercase tracking-wider">PdfTide</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">MuPDF</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Adobe Acrobat</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {[
                  ['Installation Required', '✅ None', '❌ Yes', '❌ Yes'],
                  ['File Upload to Server', '✅ Never', '✅ Never', '❌ Yes (cloud)'],
                  ['OCR Support', '✅ WASM Tesseract', '✅ Built-in', '✅ Built-in'],
                  ['Works Offline', '✅ Full', '✅ Full', '⚠️ Partial'],
                  ['Price', '✅ Free', '✅ Open-source', '❌ $24.99/mo'],
                  ['Browser-Native', '✅ Yes', '❌ No', '❌ No'],
                  ['Core Web Vitals', '✅ 100/100', '—', '—'],
                ].map(([feature, pdftide, mupdf, adobe]) => (
                  <tr key={feature} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-6 py-3.5 text-gray-700 font-medium">{feature}</td>
                    <td className="px-6 py-3.5 text-center text-indigo-700 font-medium">{pdftide}</td>
                    <td className="px-6 py-3.5 text-center text-gray-500">{mupdf}</td>
                    <td className="px-6 py-3.5 text-center text-gray-500">{adobe}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  )
}
